import React from "react";
import { ShieldCheck, FileText, Ban, CheckCircle2 } from "lucide-react";

const DISCLAIMER = `Atlas aggregates and organizes vehicle information from external sources. We do not guarantee that third-party listings are complete, accurate, current or free from errors. Users are responsible for independently verifying vehicle condition, documentation, ownership, price, availability, registration, taxes and all other relevant information before completing any transaction.

Atlas does not sell vehicles and does not handle vehicle transactions or payments.

Atlas aggregates and presents vehicle information obtained from third-party sources and automotive marketplaces across Europe and other international markets. We make reasonable efforts to organize, normalize and present listing information accurately and to identify potential inconsistencies or missing data.

However, Atlas does not create or control the original listings and cannot guarantee that all information provided by third-party sources is complete, accurate, current or free from errors.

Atlas is an information and decision-support platform and is not the seller, owner, broker or representative of any vehicle displayed through the platform.

Users should independently verify the vehicle's condition, mileage, ownership, maintenance history, legal status, price, documentation and other relevant information directly with the seller or original source before making any purchase or financial commitment.

Atlas shall not be responsible for losses, complications or decisions arising from inaccurate, incomplete, outdated or misleading information originating from third-party listings, to the extent permitted by applicable law.

Always verify the original listing and vehicle information before proceeding with a transaction.`;

const SECURITY = `Atlas will never ask users to send money to secure or purchase a vehicle. Atlas does not sell vehicles, process vehicle payments, accept deposits or act as escrow.

Atlas will never request, collect or accept payment from users in connection with a vehicle listing, vehicle purchase, reservation, inspection, transportation or transaction with a seller.

Atlas does not act as a vehicle seller, broker, payment intermediary or escrow service.

If anyone contacts you claiming to represent Atlas and asks for money, a deposit, bank transfer, cryptocurrency, gift cards, banking credentials or any other financial payment in connection with a vehicle or Atlas service, do not make any payment and do not provide financial information.

Such requests are not authorized by Atlas and should be treated as potentially fraudulent.

Always verify vehicle and seller information through the original listing source before proceeding with any transaction.`;

const DOES = ["Search", "Aggregate", "Normalize", "Analyze", "Compare", "Explain trade-offs"];
const DOES_NOT = [
  "Sell vehicles", "Take deposits or payments", "Act as escrow", "Reserve vehicles",
  "Arrange payment between buyer and seller", "Request money, crypto or gift cards",
];

export default function Trust() {
  return (
    <div>
      <section className="bg-atlas-navy text-white">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-16">
          <div className="overline text-atlas-sky mb-3">Trust &amp; safety</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
            Atlas provides intelligence.<br /><span className="text-gradient-blue">Atlas does not handle the transaction.</span>
          </h1>
          <p className="text-slate-300 mt-5 max-w-2xl leading-relaxed">
            Atlas helps you find and understand vehicle opportunities across Europe. It does not sell
            the vehicle, does not handle the transaction and never requests money from users.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-5 md:px-8 py-16 space-y-14">
        {/* What Atlas does / does not */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="border border-border rounded-md p-6 bg-card">
            <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" /> What Atlas does
            </h3>
            <ul className="space-y-2">
              {DOES.map((d) => (
                <li key={d} className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" /> {d}
                </li>
              ))}
            </ul>
          </div>
          <div className="border border-border rounded-md p-6 bg-card">
            <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2 text-red-700">
              <Ban className="h-5 w-5" /> What Atlas does not do
            </h3>
            <ul className="space-y-2">
              {DOES_NOT.map((d) => (
                <li key={d} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Ban className="h-4 w-4 text-red-500 shrink-0" /> {d}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Security notice */}
        <div id="security" className="border-2 border-atlas-blue/30 rounded-md p-6 md:p-8 bg-blue-50/40" data-testid="security-notice">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="h-6 w-6 text-atlas-blue" />
            <h2 className="font-display text-2xl font-bold">ATLAS Security Notice</h2>
          </div>
          {SECURITY.split("\n\n").map((p, i) => (
            <p key={i} className="text-sm text-slate-700 leading-relaxed mb-3">{p}</p>
          ))}
        </div>

        {/* Data disclaimer */}
        <div id="disclaimer" data-testid="data-disclaimer">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-6 w-6 text-slate-500" />
            <h2 className="font-display text-2xl font-bold">Data disclaimer</h2>
          </div>
          {DISCLAIMER.split("\n\n").map((p, i) => (
            <p key={i} className="text-sm text-muted-foreground leading-relaxed mb-3">{p}</p>
          ))}
        </div>
      </div>
    </div>
  );
}
